import { useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import socketService from '../services/socketService';

const useSocketManager = ({
  user,
  buscarSenhas,
  buscarSenhaEmAtendimento,
  calcularTempoEspera,
  setSenhaChamada,
  salvarEstadoAtendimento,
  setChamadaEmAndamento,
  setTamanhoDaFila,
  setAguardandoChamada,
  aguardandoChamada,
  senhaSelecionada,
  senhasOrdenadas,
  handleConfirmarChamarSenhaEspecifica,
  handleChamarProxima,
  // Props para aviso simples de aguardo
  setAguardandoNaFila,
  setMensagemAguardo
}) => {
  
  // Configurar o serviço de socket
  useEffect(() => {
    // Inicializar a conexão WebSocket
    socketService.connect();
    
    // Adicionar listener para o evento de reconexão
    const handleReconnect = () => {
      console.log('Socket reconectado, verificando senhas em atendimento...');
      buscarSenhaEmAtendimento();
      
      // ✅ CRÍTICO: Reconectar à room do usuário após reconexão
      if (user?.id) {
        console.log(`🔄 Resubscrevendo usuário ${user.id} à sua room após reconexão`);
        socketService.subscribeToUser(user.id);
      }
    };
    
    // Adicionar listener para conexão inicial
    const handleConnect = () => {
      console.log('Socket conectado, verificando senhas em atendimento...');
      
      // ✅ CRÍTICO: Conectar à room específica do usuário
      if (user?.id) {
        console.log(`👤 Conectando usuário ${user.id} à sua room específica: atendente:${user.id}`);
        socketService.subscribeToUser(user.id);
      }
      
      // Aguardar um momento para garantir que a conexão está estável
      setTimeout(() => {
        buscarSenhaEmAtendimento();
      }, 1000);
    };
    
    // Registrar ouvintes de eventos do socket
    const handleSenhaChamada = (data) => {
      console.log('🔊 Senha chamada recebida:', data);
      
      // ✅ REMOVER AVISO DE AGUARDO ASSIM QUE QUALQUER SENHA FOR CHAMADA NO PAINEL
      console.log('🚀 Removendo aviso de aguardo - senha chamada no painel');
      setAguardandoNaFila(false);
      setMensagemAguardo('');
      
      // Atualizar lista de senhas
      buscarSenhas();
    };
    
    const handleStatusAtualizado = (status) => {
      setChamadaEmAndamento(status.chamadaEmAndamento);
      setTamanhoDaFila(status.tamanhoDaFila);
      
      // Log de diagnóstico
      if (status.detalhes) {
        console.log(
          `Status atualizado: ${status.tamanhoDaFila} senhas na fila, ` + 
          `chamada em andamento: ${status.chamadaEmAndamento}`,
          `Detalhes da fila:`, status.detalhes.filaInfo
        );
      }
      
      // Se não há mais chamada em andamento e estávamos aguardando, tentar chamar novamente
      if (!status.chamadaEmAndamento && aguardandoChamada) {
        setAguardandoChamada(false);
        console.log('Chamada não está mais em andamento e estávamos aguardando, tentando chamar novamente...');
        // Tentar chamar novamente após um curto atraso
        setTimeout(() => {
          if (senhaSelecionada && handleConfirmarChamarSenhaEspecifica) {
            console.log('Tentando chamar senha específica novamente:', senhaSelecionada.id);
            handleConfirmarChamarSenhaEspecifica();
          } else if (senhasOrdenadas.length > 0 && handleChamarProxima) {
            console.log('Tentando chamar próxima senha na fila novamente');
            handleChamarProxima();
          }
        }, 500);
      }
    };
    
    const handleChamadaConcluida = (data) => {
      if (data.success) {
        // Mapeia os dados da senha para o formato esperado pelo frontend
        const senhaMapeada = {
          id: data.data.id,
          numero: data.data.senha,
          tipo: data.data.servico_nome,
          status: data.data.status,
          preferencial: data.data.preferencial,
          tempEspera: calcularTempoEspera(data.data.emitida_em),
          progresso: 0,
          emitida_em: data.data.emitida_em
        };
        
        // Verifica se esta senha foi chamada pelo usuário atual
        if (data.data.usuario_id === user?.id) {
          // Atualiza a senha atual com os dados mapeados
          setSenhaChamada(senhaMapeada);
          
          // Salvar no localStorage
          salvarEstadoAtendimento(senhaMapeada, false, false, null);
        }
        
        // Atualiza a lista de senhas aguardando
        buscarSenhas();
      }
    };
    
    const handleChamadaEnfileirada = (data) => {
      // Marcar que estamos aguardando nossa vez
      setAguardandoChamada(true);
      // Atualizar o status usando a versão segura
      socketService.solicitarStatusChamadaSeguro().catch(error => {
        console.warn('Erro ao solicitar status após chamada enfileirada:', error);
      });
    };

    // Handler simples para aviso de aguardo na fila
    const handleSenhaAguardandoNaFila = (data) => {
      console.log('🔄 Senha aguardando na fila:', data);
      
      // Ativar aviso simples
      setAguardandoNaFila(true);
      setMensagemAguardo(data.mensagem || `Sua senha está na fila! Posição: ${data.posicaoFila}/${data.totalNaFila}`);
      
      // Marcar que estamos aguardando
      setAguardandoChamada(true);
      
      // Mostrar toast informativo
      toast.info(data.mensagem || `Sua senha está na fila! Posição: ${data.posicaoFila}/${data.totalNaFila}`, {
        autoClose: 5000,
        position: 'top-center'
      });
    };

    // Handler para quando a senha é realmente chamada (ocultar aviso)
    const handleSenhaRealmenteChamada = (data) => {
      console.log('🚀 Senha realmente chamada (vocalizada):', data);
      
      // Remover aviso de aguardo
      setAguardandoNaFila(false);
      setMensagemAguardo('');
      
      // Marcar que não estamos mais aguardando
      setAguardandoChamada(false);
      
      // Mostrar toast de confirmação
      toast.success(data.mensagem || `Sua senha ${data.numero} foi chamada!`, {
        autoClose: 5000,
        position: 'top-center'
      });
      
      // Atualizar lista de senhas
      buscarSenhas();
    };
    
    // Registrar os listeners
    socketService.on('reconnect', handleReconnect);
    socketService.on('connect', handleConnect);
    
    // Registrar ouvintes de eventos do socket
    socketService.on('senhaChamada', handleSenhaChamada);
    socketService.on('statusChamadaAtualizado', handleStatusAtualizado);
    socketService.on('chamadaConcluida', handleChamadaConcluida);
    socketService.on('chamadaEnfileirada', handleChamadaEnfileirada);
    socketService.on('senhaAtualizada', handleSenhaChamada);
    
    // Registrar listeners para aviso de aguardo
    socketService.on('senhaAguardandoNaFila', handleSenhaAguardandoNaFila);
    socketService.on('senhaRealmenteChamada', handleSenhaRealmenteChamada);
    
    // Listeners para eventos de rechamada
    socketService.on('senhaRechamada', (data) => {
      console.log('Senha rechamada com sucesso:', data);
    });
    
    socketService.on('rechamarErro', (data) => {
      console.error('Erro ao rechamar senha:', data);
      toast.error(`Erro ao rechamar senha: ${data.error}`);
    });

    // ==========================================
    // HANDLERS DE TRANSFERÊNCIA DE SENHAS
    // ==========================================

    // Handler para quando uma senha é transferida para o usuário atual
    const handleSenhaTransferida = (data) => {
      console.log('🚨 CRÍTICO: Senha transferida recebida pelo WebSocket!', {
        data,
        userId: user?.id,
        timestamp: new Date().toISOString()
      });
      
      if (data.senha) {
        // Mapear para o formato esperado pelo frontend
        const senhaMapeada = {
          id: data.senha.id,
          numero: data.senha.numero,
          tipo: data.senha.servicoNome,
          status: data.senha.status,
          guiche: data.senha.guiche,
          transferidaDe: data.senha.transferidaDe,
          motivo: data.senha.motivo,
          tempEspera: calcularTempoEspera(new Date()),
          preferencial: false,
          progresso: 0
        };

        // ✅ NOTIFICAÇÃO REMOVIDA: Centralizada no socketService via notificationManager
        console.log('📤 Notificação de transferência centralizada no socketService');

        // ✅ CRÍTICO: Tocar som específico de transferência
        if (socketService.soundEnabled) {
          socketService.playTransferNotificationSound();
        }

        console.log('🔄 Atualizando dados após receber transferência...');
        
        // Chamar buscarSenhas para atualizar a lista
        buscarSenhas();
        
        // Se o usuário não tem senha em atendimento, buscar novamente
        buscarSenhaEmAtendimento();
        
        console.log('✅ Dados atualizados após transferência!');
      } else {
        console.warn('⚠️ Evento senhaTransferida sem dados de senha:', data);
      }
    };

    // Handler para confirmação de transferência enviada
    const handleTransferenciaConfirmada = (data) => {
      console.log('✅ Transferência confirmada:', data);
      
      if (data.destinatario) {
        // ✅ NOTIFICAÇÃO REMOVIDA: Centralizada no socketService via notificationManager
        console.log(`✅ Transferência confirmada para ${data.destinatario.nome}`);

        // Atualizar listas após transferência bem-sucedida
        buscarSenhas();
        buscarSenhaEmAtendimento();
      }
    };

    // Handler para erro de transferência
    const handleErroTransferencia = (data) => {
      console.error('❌ Erro na transferência:', data);
      
      // ✅ NOTIFICAÇÃO REMOVIDA: Centralizada no socketService via notificationManager
      console.error(`❌ Erro ao transferir senha: ${data.error}`);
    };

    // Registrar listeners de transferência
    socketService.on('senhaTransferida', handleSenhaTransferida);
    socketService.on('transferenciaConfirmada', handleTransferenciaConfirmada);
    socketService.on('erroTransferencia', handleErroTransferencia);
    
    // ✅ CRÍTICO: Garantir que o usuário está conectado à sua room se já estiver conectado
    if (user?.id && socketService.isConnected()) {
      console.log(`👤 Socket já conectado, subscrevendo usuário ${user.id} à sua room: atendente:${user.id}`);
      socketService.subscribeToUser(user.id);
    }
    
    // Verificar o status inicial da fila usando a versão segura
    const checkInitialStatus = () => {
      socketService.solicitarStatusChamadaSeguro(10000).catch(error => {
        console.warn('Erro ao verificar status inicial:', error);
      });
    };
    
    checkInitialStatus();
    
    // Buscar senha em atendimento imediatamente se já conectado
    if (socketService.connected) {
      console.log('Socket já conectado, buscando senha em atendimento...');
      setTimeout(() => {
        buscarSenhaEmAtendimento();
      }, 500);
    }
    
    // Limpar ouvintes ao desmontar
    return () => {
      socketService.off('senhaChamada', handleSenhaChamada);
      socketService.off('statusChamadaAtualizado', handleStatusAtualizado);
      socketService.off('chamadaConcluida', handleChamadaConcluida);
      socketService.off('chamadaEnfileirada', handleChamadaEnfileirada);
      socketService.off('senhaAtualizada', handleSenhaChamada);
      socketService.off('senhaRechamada');
      socketService.off('rechamarErro');
      socketService.off('reconnect', handleReconnect);
      socketService.off('connect', handleConnect);
      socketService.off('senhaTransferida', handleSenhaTransferida);
      socketService.off('transferenciaConfirmada', handleTransferenciaConfirmada);
      socketService.off('erroTransferencia', handleErroTransferencia);
      socketService.off('senhaAguardandoNaFila', handleSenhaAguardandoNaFila);
      socketService.off('senhaRealmenteChamada', handleSenhaRealmenteChamada);
    };
  }, [
    aguardandoChamada,
    senhaSelecionada,
    user,
    buscarSenhas,
    buscarSenhaEmAtendimento,
    senhasOrdenadas,
    calcularTempoEspera,
    setSenhaChamada,
    salvarEstadoAtendimento,
    setChamadaEmAndamento,
    setTamanhoDaFila,
    setAguardandoChamada,
    handleConfirmarChamarSenhaEspecifica,
    handleChamarProxima,
    setAguardandoNaFila,
    setMensagemAguardo
  ]);

  // Função para chamar senha via WebSocket
  const chamarSenhaViaSocket = useCallback((senhaId, iniciarAutomaticamente = false) => {
    try {
      socketService.chamarSenha(
        senhaId, 
        user?.id, 
        user?.guiche || 1,
        iniciarAutomaticamente
      );
      return true;
    } catch (error) {
      console.error('Erro ao solicitar chamada da senha:', error);
      toast.error('Erro ao solicitar chamada da senha. Por favor, tente novamente.');
      return false;
    }
  }, [user]);

  // Função para rechamar senha via WebSocket
  const rechamarSenhaViaSocket = useCallback((numeroSenha, guiche, preferencial = false) => {
    try {
      console.log('Iniciando rechamada da senha:', {
        numero: numeroSenha,
        guiche: guiche,
        preferencial: preferencial
      });

      const sucesso = socketService.rechamarSenha(
        numeroSenha,
        guiche,
        preferencial
      );

      if (sucesso) {
        console.log('Solicitação de rechamada enviada com sucesso');
        return true;
      } else {
        toast.error('Erro ao conectar com o servidor. Verifique sua conexão.');
        console.error('Falha ao enviar solicitação de rechamada');
        return false;
      }
    } catch (error) {
      console.error('Erro ao rechamar senha:', error);
      toast.error('Erro ao rechamar senha. Por favor, tente novamente.');
      return false;
    }
  }, []);

  return {
    chamarSenhaViaSocket,
    rechamarSenhaViaSocket
  };
};

export default useSocketManager;